// src/pages/CourseList.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Book, ArrowLeft, Loader, Search, FileText, 
  Users, BarChart, GraduationCap 
} from 'lucide-react';
import { publicAPI } from '../services/api';
import Header from '../components/common/Header';

const CourseList = () => {
  const { schoolId, departmentId, year } = useParams();
  const navigate = useNavigate();
  
  const [courses, setCourses] = useState([]);
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [deptRes, coursesRes] = await Promise.all([
          publicAPI.getDepartmentById(departmentId),
          publicAPI.getCourses({ 
            department: departmentId, 
            level: year,
            limit: 100 
          })
        ]);

        if (deptRes.success) setDepartment(deptRes.data);
        if (coursesRes.success) setCourses(coursesRes.data || []);
        
      } catch (error) {
        console.error("Error loading courses:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [departmentId, year]);

  const filteredCourses = courses.filter(course => 
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.courseCode.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header />
      
      <div className="bg-white border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <Link to={`/school/${schoolId}`} className="hover:text-blue-600">School</Link>
            <span>/</span>
            <Link to={`/school/${schoolId}/department/${departmentId}`} className="hover:text-blue-600">{department?.code || 'Dept'}</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">Year {year}</span>
          </div>
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <GraduationCap className="w-6 h-6 text-blue-600" /> Year {year} Courses
              </h1>
              <p className="text-gray-500 text-sm mt-1">Found {loading ? '...' : filteredCourses.length} courses</p>
            </div>
            
            <div className="w-full md:w-72 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input 
                type="text"
                placeholder="Search by name or code..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm h-[220px] p-6 flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-start mb-4">
                    <div className="h-6 w-20 bg-gray-200 rounded-lg"></div>
                    <div className="h-6 w-24 bg-gray-150 rounded-lg"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-3"></div>
                  <div className="h-4 bg-gray-100 rounded w-5/6"></div>
                </div>
                <div className="pt-4 border-t border-gray-100 flex justify-between items-center">
                  <div className="h-4 bg-gray-255 rounded w-28"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredCourses.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <div 
                key={course._id}
                // ✅ UPDATE: Navigate to Teacher Selection Page
                onClick={() => navigate(`/school/${schoolId}/department/${departmentId}/year/${year}/course/${course._id}`)}
                className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-blue-200 transition-all duration-300 cursor-pointer group flex flex-col h-full"
              >
                <div className="p-6 flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <span className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-bold rounded-lg uppercase">{course.courseCode}</span>
                    <span className="flex items-center gap-1.5 text-slate-500 text-xs font-black bg-slate-50 px-3 py-1 rounded-lg">
                      <BarChart className="w-3.5 h-3.5 text-blue-600" /> {course.credits || 3} ECTS
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">{course.title}</h3>
                  <p className="text-gray-600 text-sm line-clamp-3 mb-4">{course.description || 'No description available.'}</p>
                </div>
                <div className="bg-gray-50/50 px-6 py-4 border-t border-gray-100 rounded-b-xl flex justify-between items-center mt-auto">
                  <div className="flex items-center gap-4 text-xs font-medium text-gray-500">
                    <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Materials Available</span>
                  </div>
                  <span className="text-blue-600 text-sm font-semibold group-hover:translate-x-1 transition-transform">Select Instructor</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-dashed mx-auto max-w-2xl">
            <Book className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900">No Courses Found</h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseList;